import "../../components/EventUserDashboard.css";

import React, { useEffect, useState } from "react";
import { Select } from "@mantine/core";

/* MATCH BACKEND ENUM */
enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface PersonDto {
    personId: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
}

interface BarStatusDto {
    bar: number;
    functionDrinks: number;
    personalDrinks: number;
    totalAllowed: number;
    usedDrinks: number;
    remainingDrinks: number;
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return "Ukendt";
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.persons)) return result.persons;
    if (Array.isArray(result?.bars)) return result.bars;
    if (Array.isArray(result?.results)) return result.results;
    return [];
};

const toNonNegativeNumber = (value: string | number): number => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return 0;
    return Math.floor(num);
};

const AdminPersonExtraDrinks: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);
    const [eventName, setEventName] = useState<string>("");

    const [persons, setPersons] = useState<PersonDto[]>([]);
    const [personId, setPersonId] = useState<number>(0);

    const [status, setStatus] = useState<BarStatusDto[]>([]);

    const [bar1Extra, setBar1Extra] = useState<number>(0);
    const [bar2Extra, setBar2Extra] = useState<number>(0);
    const [bar3Extra, setBar3Extra] = useState<number>(0);

    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingPersons, setLoadingPersons] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const resetExtras = () => {
        setBar1Extra(0);
        setBar2Extra(0);
        setBar3Extra(0);
    };

    const getPersonLabel = (p: PersonDto): string => {
        if (p.fullName && p.fullName.trim().length > 0) {
            return p.fullName.trim();
        }

        return `${p.firstName || ""} ${p.lastName || ""}`.trim() || `Person ${p.personId}`;
    };

    // ---------------- HENT EVENTS ----------------
    useEffect(() => {
        const loadEvents = async () => {
            try {
                setLoadingEvents(true);
                const res = await fetch("/api/events");
                if (!res.ok) {
                    throw new Error(`Kunne ikke hente events (${res.status})`);
                }

                const result = await res.json();
                const data = toArray<EventDto>(result);
                setEvents(data);
            } catch (err) {
                console.error("Fejl ved hentning af events:", err);
                setError("Kunne ikke hente events");
                setEvents([]);
            } finally {
                setLoadingEvents(false);
            }
        };

        loadEvents();
    }, []);

    // ---------------- EVENT CHANGE ----------------
    const handleEventChange = async (id: number) => {
        setEventId(id);
        setPersonId(0);
        setPersons([]);
        setStatus([]);
        resetExtras();
        setMessage(null);
        setError(null);

        if (!id) {
            setEventName("");
            return;
        }

        const selected = events.find((e) => e.eventID === id);
        setEventName(selected?.eventName || `Event ${id}`);

        try {
            setLoadingPersons(true);

            const res = await fetch(`/api/events/${id}/persons`);
            if (!res.ok) {
                throw new Error(`Kunne ikke hente personer (${res.status})`);
            }

            const result = await res.json();
            const data = toArray<PersonDto>(result);
            setPersons(data);
        } catch (err) {
            console.error("Fejl ved hentning af personer:", err);
            setError("Kunne ikke hente personer for event");
            setPersons([]);
        } finally {
            setLoadingPersons(false);
        }
    };

    // ---------------- PERSON CHANGE ----------------
    const handlePersonChange = async (value: string | null) => {
        const id = value ? Number(value) : 0;

        setPersonId(id);
        setStatus([]);
        resetExtras();
        setMessage(null);
        setError(null);

        if (!id || !eventId) return;

        try {
            setLoadingStatus(true);

            const res = await fetch(
                `/api/drinks/status?personId=${id}&eventId=${eventId}`
            );

            if (!res.ok) {
                throw new Error(`Kunne ikke hente status (${res.status})`);
            }

            const result = await res.json();
            const data = toArray<BarStatusDto>(result);

            setStatus(data);

            data.forEach((s) => {
                if (s.bar === Bar.BAR1) setBar1Extra(toNonNegativeNumber(s.personalDrinks));
                if (s.bar === Bar.BAR2) setBar2Extra(toNonNegativeNumber(s.personalDrinks));
                if (s.bar === Bar.BAR3) setBar3Extra(toNonNegativeNumber(s.personalDrinks));
            });
        } catch (err) {
            console.error("Fejl ved hentning af drinkstatus:", err);
            setError("Kunne ikke hente drinkstatus");
            setStatus([]);
            resetExtras();
        } finally {
            setLoadingStatus(false);
        }
    };

    // ---------------- GEM ----------------
    const submit = async () => {
        if (!eventId || !personId) {
            setError("Event og person skal vælges");
            return;
        }

        setMessage(null);
        setError(null);
        setIsSaving(true);

        const bars = [
            { bar: Bar.BAR1, maxDrinks: toNonNegativeNumber(bar1Extra) },
            { bar: Bar.BAR2, maxDrinks: toNonNegativeNumber(bar2Extra) },
            { bar: Bar.BAR3, maxDrinks: toNonNegativeNumber(bar3Extra) }
        ];

        try {
            for (const b of bars) {
                const res = await fetch("/api/admin/drinks-person", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        personId,
                        eventId,
                        bar: b.bar,
                        maxDrinks: b.maxDrinks
                    })
                });

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || `Gemning fejlede (${res.status})`);
                }
            }

            setMessage("Ekstra drinks opdateret");
            await handlePersonChange(String(personId));
        } catch (err) {
            console.error("Fejl ved gemning:", err);
            setError("Fejl ved gemning");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">
                        {eventName || "Vælg event"}
                    </div>
                    <div className="meta">
                        {personId ? "Juster drinks pr. bar" : "Vælg event og person"}
                    </div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    {message && (
                        <div className="statusBadge" style={{ padding: 8, marginBottom: 16 }}>
                            {message}
                        </div>
                    )}

                    <div className="sectionTitle">Valg</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <Select
                                placeholder={loadingEvents ? "Henter events..." : "Vælg event"}
                                value={eventId ? String(eventId) : null}
                                onChange={(val) => handleEventChange(val ? Number(val) : 0)}
                                data={events.map((e) => ({
                                    value: String(e.eventID),
                                    label: e.eventName || `Event ${e.eventID}`
                                }))}
                                disabled={loadingEvents}
                                clearable
                            />
                        </div>

                        {eventId > 0 && (
                            <div className="stat" style={{ padding: 16 }}>
                                <Select
                                    searchable
                                    clearable
                                    placeholder={loadingPersons ? "Henter personer..." : "Vælg person"}
                                    value={personId ? String(personId) : null}
                                    onChange={handlePersonChange}
                                    data={persons.map((p) => ({
                                        value: String(p.personId),
                                        label: getPersonLabel(p)
                                    }))}
                                    disabled={loadingPersons}
                                />
                            </div>
                        )}
                    </div>

                    {loadingStatus && (
                        <div style={{ marginTop: 20 }}>
                            Henter drinkstatus...
                        </div>
                    )}

                    {status.length > 0 && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Status pr. bar
                            </div>

                            <div className="statsGrid3" style={{ marginTop: 12 }}>
                                {status.map((s) => (
                                    <div key={s.bar} className="stat" style={{ padding: 16 }}>
                                        <div style={{ fontWeight: 800 }}>
                                            {getBarName(s.bar)}
                                        </div>

                                        <div style={{ marginTop: 10 }}>
                                            <div>Function: {s.functionDrinks}</div>
                                            <div>Personlig: {s.personalDrinks}</div>
                                            <div>Total: {s.totalAllowed}</div>
                                            <div>Brugt: {s.usedDrinks}</div>
                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    fontWeight: 800,
                                                    color: s.remainingDrinks <= 0 ? "#b0000d" : "#007a41"
                                                }}
                                            >
                                                Tilbage: {s.remainingDrinks}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {personId > 0 && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Juster ekstra drinks
                            </div>

                            <div className="statsGrid3" style={{ marginTop: 12 }}>
                                <div className="stat" style={{ padding: 16 }}>
                                    <label>VIP Lounge</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar1Extra}
                                        onChange={(e) => setBar1Extra(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <label>Hygge Lounge</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar2Extra}
                                        onChange={(e) => setBar2Extra(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <label>Jail House Bar</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar3Extra}
                                        onChange={(e) => setBar3Extra(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                style={{
                                    marginTop: 20,
                                    width: "100%",
                                    opacity: isSaving ? 0.7 : 1,
                                    cursor: isSaving ? "not-allowed" : "pointer"
                                }}
                                onClick={submit}
                                disabled={isSaving}
                            >
                                {isSaving ? "Gemmer..." : "Gem ændringer"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPersonExtraDrinks;